import './AnalyticsCoCustomer.css';
import React, { useMemo, useRef } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { clsx } from 'clsx';
import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery } from 'src/services';
import { DateItems } from 'src/pages/AnalyticsPage/AnalyticsCustomer/DateItems';
import { CollapsibleCard } from 'src/components/CollapsibleCard/CollapsibleCard';
import { AnalyticsControlsHeader } from 'src/pages/AnalyticsPage/AnalyticsCustomer/components/AnalyticsControlsHeader';
import { AiPerformanceOverviewSection } from 'src/pages/AnalyticsPage/AnalyticsCustomer/components/AiPerformanceOverviewSection';
import { EscalationsOverviewSection } from 'src/pages/AnalyticsPage/AnalyticsCustomer/components/EscalationsOverviewSection';
import { SpeedEfficiencySection } from 'src/pages/AnalyticsPage/AnalyticsCustomer/components/SpeedEfficiencySection';
import { useCoCustomerDateRange } from 'src/pages/AnalyticsPage/AnalyticsCustomer/hooks/useCoCustomerDateRange';
import { useCoCustomerAggregatedMetrics } from 'src/pages/AnalyticsPage/AnalyticsCustomer/hooks/useCoCustomerAggregatedMetrics';
import { useCoCustomerTimeSeries } from 'src/pages/AnalyticsPage/AnalyticsCustomer/hooks/useCoCustomerTimeSeries';
import { useInProgressTickets } from 'src/pages/AnalyticsPage/AnalyticsCustomer/hooks/useInProgressTickets';
import { useAnalyticsLoader } from 'src/pages/AnalyticsPage/AnalyticsCustomer/hooks/useAnalyticsLoader';

const AnalyticsCoCustomer = () => {
  const { id } = useParams();
  const isLgDown = useMediaQuery('(max-width: 992px)');
  const statRef = useRef(null);

  const { data: currentProject } = useGetProjectByIdQuery({ id }) as any;
  const isProjectReady = Boolean(currentProject);

  const { dateRange, setDateRange, aggregatedRequestParams, timeSeriesRequestParams } =
    useCoCustomerDateRange(id);

  const {
    metrics,
    aggregatedData,
    isFetching: isFetchingAggregated,
  } = useCoCustomerAggregatedMetrics(aggregatedRequestParams, isProjectReady);

  const { timeSeriesResponse, isFetching: isFetchingTimeSeries } = useCoCustomerTimeSeries(
    timeSeriesRequestParams,
    isProjectReady,
  );

  const inProgressTickets = useInProgressTickets(id, dateRange);

  useAnalyticsLoader(isFetchingAggregated, isFetchingTimeSeries);

  const dateRangeOptions = useMemo(
    () =>
      DateItems.map((item) => ({
        label: item.label,
        disabled: item.disabled,
        default: item.default,
      })),
    [],
  );

  return (
    <PageContentWrapper
      className={clsx('cocustomer-analytics-page-container', !isLgDown && 'relative')}
      showFilter
      projectBreadcrumbsContent={'Analytics'}
      isMobileMarginTop={false}
      ref={statRef}
    >
      <AnalyticsControlsHeader
        onDateChange={setDateRange}
        dateRangeOptions={dateRangeOptions}
        statRef={statRef}
      />
      <div className="z-10 gap-[10px] flex flex-col relative overflow-auto h-full">
        <CollapsibleCard title={'AI Performance Overview'}>
          <AiPerformanceOverviewSection
            dateRange={dateRange}
            resolutionRate={metrics.resolutionRate}
            resolvedTickets={metrics.resolvedTickets}
            escalatedTickets={metrics.escalatedTickets}
            inProgressTickets={inProgressTickets}
            totalProcessedTickets={metrics.totalProcessedTickets}
            aiRepliesSent={metrics.aiRepliesSent}
            aiRepliesPerProcessedTicket={metrics.aiRepliesPerProcessedTicket}
            timeSeriesResponse={timeSeriesResponse}
          />
        </CollapsibleCard>
        <CollapsibleCard title={'Escalations Overview'}>
          <EscalationsOverviewSection
            dateRange={dateRange}
            aggregatedData={aggregatedData}
            timeSeriesResponse={timeSeriesResponse}
          />
        </CollapsibleCard>
        <CollapsibleCard title={'Speed & Efficiency'}>
          <SpeedEfficiencySection
            dateRange={dateRange}
            firstResponseTimeAvg={metrics.firstResponseTimeAvg}
            responseTimeAvg={metrics.responseTimeAvg}
            resolutionTimeAvg={metrics.resolutionTimeAvg}
          />
        </CollapsibleCard>
      </div>
    </PageContentWrapper>
  );
};

export default AnalyticsCoCustomer;
